import { AccountType, EffectiveSubscriptionState, Permission } from '@ee/common/enums';

/**
 * Effective subscription states in which an attorney org retains full access to the portal.
 *
 * Single source of truth: this list used to be copy-pasted into the route guard, the user resolver
 * and the auth effect, and the three copies were free to drift.
 */
export const SUBSCRIPTION_ACTIVE_STATES: EffectiveSubscriptionState[] = [
  EffectiveSubscriptionState.ACTIVE,
  EffectiveSubscriptionState.TRIAL,
  EffectiveSubscriptionState.FREE,
  EffectiveSubscriptionState.GRACE_PERIOD
];

/**
 * The permissions that let a user reach the billing screens (`/plan`, `/admin/billing`) and fix a
 * lapsed subscription. Note the built-in attorney "Admin" permission group holds NEITHER — only
 * "Account Admin" does (see PermissionGroupUtils.generateAttorneyAdminGroup on the backend).
 */
export const BILLING_PERMISSIONS: string[] = [Permission.ADMIN_BILLING_VIEW, Permission.ADMIN_BILLING_EDIT];

/**
 * Whether the org's subscription still grants access. The subscription gate is attorney-only: client
 * and property-manager orgs are never blocked on it, so they always read as active here.
 *
 * Accepts any object with the two fields it reads, to avoid a circular dependency between
 * @ee/common/utils and @ee/common/models.
 */
export function isSubscriptionActive(
  org: { type?: AccountType; effective_subscription_state?: EffectiveSubscriptionState } | null | undefined
): boolean {
  if (org?.type !== AccountType.ATTORNEY) {
    return true;
  }
  return SUBSCRIPTION_ACTIVE_STATES.includes(org.effective_subscription_state);
}

/**
 * Whether the user may act on a subscription problem — i.e. whether pointing them at the billing
 * screens is useful rather than a dead end.
 *
 * VIEW *or* EDIT, deliberately: `/plan` declares ADMIN_BILLING_EDIT while `/admin/billing` declares
 * ADMIN_BILLING_VIEW, so gating the escape hatch on only one of them strands holders of the other.
 */
export function canManageBilling(user: { permissions?: string[] } | null | undefined): boolean {
  return user?.permissions?.some(permission => BILLING_PERMISSIONS.includes(permission)) || false;
}
